import React from 'react';
import { Controller, type ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WarningAmberRounded } from '@mui/icons-material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from 'shared/components/alert/alert';

import type { LoginFormInput } from 'features/login/models/login-form-input';
import { useLoginForm } from './hooks';
import styles from './login-form.module.scss';

const LoginForm = (): React.JSX.Element => {
  const { t } = useTranslation('login');
  const { control, handleSubmit, formState, getRegisterOptions, login, loginIsLoading, lastEnteredEmailDomain, isMaintenance } =
    useLoginForm();

  const registerOptions = getRegisterOptions();

  const renderEmailField = (field: ControllerRenderProps<LoginFormInput, 'email'>) => (
    <TextField
      {...field}
      label={t('emailInputLabelText')}
      variant="outlined"
      error={!!formState.errors.email}
      helperText={formState.errors.email?.message?.toString()}
      className={styles.formInput}
      disabled={isMaintenance}
      sx={{
        '& .MuiInputBase-root': {
          borderRadius: 32,
          '& input': {
            textAlign: lastEnteredEmailDomain?.length > 0 ? 'right' : 'left',
          },
        },
      }}
    />
  );

  return (
    <form onSubmit={handleSubmit(login)}>
      <div className={styles.loginFormContainer}>
        <div className={styles.loginFormTitleContainer}>
          <Typography variant="h2">{t('loginTitle')}</Typography>
          <Typography variant="h4">{t('loginSubTitle')}</Typography>
          {isMaintenance && (
            <Alert severity="error" title={t('maintenanceTitle')} body={t('maintenanceBody')} icon={<WarningAmberRounded />} />
          )}
        </div>
        <Controller
          name="email"
          control={control}
          defaultValue=""
          rules={registerOptions.email}
          render={({ field }) => renderEmailField(field)}
        />
        <Button
          className={styles.loginButton}
          type="submit"
          variant="contained"
          color="primary"
          loading={loginIsLoading}
          loadingPosition="center"
          disabled={isMaintenance}
        >
          {t('loginButtonText')}
          <ChevronRight />
        </Button>
      </div>
    </form>
  );
};

export default LoginForm;
